import { z } from 'zod';

import { MATCH_REASON_CODES } from '../constants/matching';
import { MatchProposalStatusSchema, WeekdaySchema } from '../enums';
import { IdSchema } from './common';
import { ActivityBriefSchema } from './pool';

/**
 * Контракт `admin.matching.*` (BLUEPRINT Stage 10, задачи 3–4).
 * Предложение отряда хранится в `MatchProposal`; тексты объяснений — в `texts.ts` админки.
 */

export const MatchReasonSchema = z.object({
  code: z.enum(MATCH_REASON_CODES),
  value: z.number(),
  detail: z.number(),
});
export type MatchReasonDto = z.infer<typeof MatchReasonSchema>;

/** `MatchProposal.params` — всё, чем создаётся отряд при утверждении. */
export const MatchProposalParamsSchema = z.object({
  cellKey: z.string(),
  activityId: IdSchema,
  squadSize: z.number().int().positive(),
  minToLaunch: z.number().int().positive(),
  weekday: WeekdaySchema,
  startTime: z.string(),
  levelBand: z.number().int().min(1).max(5),
  ageMin: z.number().int(),
  ageMax: z.number().int(),
  districtIds: z.array(IdSchema),
  applicationIds: z.array(IdSchema),
  reasons: z.array(MatchReasonSchema),
});
export type MatchProposalParams = z.infer<typeof MatchProposalParamsSchema>;

export const MatchProposalMemberSchema = z.object({
  id: IdSchema,
  name: z.string(),
  username: z.string().nullable(),
  age: z.number().int().nullable(),
  level: z.number().int().nullable(),
  districtNames: z.array(z.string()),
  days: z.array(WeekdaySchema),
  reliability: z.number().int(),
  streak: z.number().int(),
  /** Ждёт в пуле столько дней */
  waitingDays: z.number().int().nonnegative(),
  /** Seed предложения — вокруг него собран состав */
  isSeed: z.boolean(),
});
export type MatchProposalMember = z.infer<typeof MatchProposalMemberSchema>;

export const MatchProposalRowSchema = z.object({
  id: IdSchema,
  status: MatchProposalStatusSchema,
  city: z.object({ id: IdSchema, name: z.string() }),
  activity: ActivityBriefSchema,
  params: MatchProposalParamsSchema,
  score: z.number(),
  members: z.array(MatchProposalMemberSchema),
  /** Отряд, созданный при утверждении */
  squadId: IdSchema.nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  /** Кандидат уже попал в отряд или ушёл из пула — предложение неактуально */
  stale: z.boolean(),
});
export type MatchProposalRow = z.infer<typeof MatchProposalRowSchema>;

export const AdminMatchingListInputSchema = z.object({
  cityId: IdSchema.optional(),
  status: z.array(MatchProposalStatusSchema).optional(),
});
export type AdminMatchingListInput = z.infer<typeof AdminMatchingListInputSchema>;

export const AdminMatchingRunInputSchema = z.object({
  /** Пусто — все активные города */
  cityId: IdSchema.optional(),
});
export type AdminMatchingRunInput = z.infer<typeof AdminMatchingRunInputSchema>;

export const MatchingRunResultSchema = z.object({
  created: z.number().int().nonnegative(),
  /** Предложения, снятые как неактуальные */
  dropped: z.number().int().nonnegative(),
  candidates: z.number().int().nonnegative(),
  transfersOffered: z.number().int().nonnegative(),
  longWaitNotified: z.number().int().nonnegative(),
  ms: z.number().int().nonnegative(),
});
export type MatchingRunResult = z.infer<typeof MatchingRunResultSchema>;

export const AdminMatchingApproveInputSchema = z.object({
  proposalId: IdSchema,
  /** Имя отряда; пусто — сгенерируется из активности и вечера */
  name: z.string().trim().min(2).max(60).nullable().default(null),
  /** Создать черновик сезона по шаблону и открыть продажи */
  openSale: z.boolean().default(true),
  /** Код шаблона сезона; пусто — шаблон по умолчанию */
  templateCode: z.string().nullable().default(null),
});
export type AdminMatchingApproveInput = z.infer<typeof AdminMatchingApproveInputSchema>;

export const AdminMatchingRejectInputSchema = z.object({
  proposalId: IdSchema,
  comment: z.string().trim().max(500).nullable().default(null),
});
export type AdminMatchingRejectInput = z.infer<typeof AdminMatchingRejectInputSchema>;

/** Метрики пула для админки (BLUEPRINT Stage 10, задача 4). */
export const PoolMetricsCellSchema = z.object({
  cellKey: z.string(),
  activity: ActivityBriefSchema,
  squadSize: z.number().int().positive(),
  minToLaunch: z.number().int().positive(),
  weekday: WeekdaySchema,
  levelBand: z.number().int(),
  count: z.number().int().nonnegative(),
  /** Медианное время ожидания в этой ячейке, дней */
  medianWaitDays: z.number().nonnegative(),
});
export type PoolMetricsCell = z.infer<typeof PoolMetricsCellSchema>;

export const PoolMetricsSchema = z.object({
  candidates: z.number().int().nonnegative(),
  cells: z.number().int().nonnegative(),
  /** Медианное время в пуле по всем ожидающим, дней */
  medianWaitDays: z.number().nonnegative(),
  /** Доля ячеек, набравших свой порог запуска (`minToLaunch`), 0–1; раньше — «шесть и больше» (ADR-37) */
  denseCellsShare: z.number().min(0).max(1),
  denseCells: z.number().int().nonnegative(),
  /** Кандидатов, ждущих дольше `MATCHING.longWaitDays` */
  longWait: z.number().int().nonnegative(),
  top: z.array(PoolMetricsCellSchema),
});
export type PoolMetrics = z.infer<typeof PoolMetricsSchema>;
